import { type FC, type ReactNode, useCallback } from "react";

import { css } from "@emotion/css";
import { Dropdown, Menu, MenuItem } from "@snapchat/snap-design-system";

import { type OnTimeRangeChange, useTimeRange } from "../../hooks";
import { GranularityOptions, type LensGranularity } from "../../models";
import { DropdownItem } from "../shared";

const menuStyles = css`
    width: max-content;
    min-width: 220px;
    max-width: calc(100vw - 2 * var(--spacing-lg));
`;

interface SelectTimeRangeProps {
    selection?: {
        options: readonly { value: string; label: string; detail?: ReactNode }[];
        value: string;
        onChange: (value: string) => void;
    };
    defaultRange?: LensGranularity;
    onTimeRangeChange?: OnTimeRangeChange;
}

export const SelectTimeRange: FC<SelectTimeRangeProps> = ({ defaultRange, onTimeRangeChange, selection }) => {
    const { selectedTimeRange, selectedLastTimeRangeLabel, setSelectedTimeRange } = useTimeRange({
        defaultRange,
        onTimeRangeChange,
    });

    const options: readonly { value: string; label: string; detail?: ReactNode }[] =
        selection?.options ?? GranularityOptions;
    const selected = selection?.value ?? selectedTimeRange;
    const label = selection
        ? options.find(({ value }) => value === selected)?.label ?? "-"
        : selectedLastTimeRangeLabel;
    const onChange = useCallback(
        (value: string): void => {
            if (selection) selection.onChange(value);
            else {
                const option = GranularityOptions.find((option) => String(option.value) === value);
                if (option) setSelectedTimeRange(option.value);
            }
        },
        [selection, setSelectedTimeRange],
    );

    return (
        <Dropdown
            overlay={
                <Menu className={menuStyles}>
                    {options.map(({ value, label, detail }) => (
                        <MenuItem
                            key={value}
                            onClick={() => {
                                onChange(value);
                            }}
                        >
                            <DropdownItem
                                label={label}
                                detail={detail}
                                isSelected={selected === value}
                                highlightSelection={Boolean(selection)}
                            />
                        </MenuItem>
                    ))}
                </Menu>
            }
            buttonProps={{ label, type: "default", size: selection ? "small" : "regular" }}
        />
    );
};
